import React, {useEffect, useState, useMemo, useCallback, useRef} from 'react';
import {useParams, Link as RouterLink, useNavigate} from 'react-router-dom';
import {
    useMaterialReactTable,
    MaterialReactTable,
} from 'material-react-table';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Grid,
    Chip,
    Button,
    Alert,
    List,
    ListItem,
    Divider,
    Link,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    FormControl,
    InputLabel,
    Select,
    MenuItem, TextField, DialogActions, FormHelperText, Snackbar
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import apiService from '../../services/apiService';
import {getStatusColor, getWinningColor} from '../../utils/formatters';
import {ROLES} from "../../config/roles";
import PlayersPage from "./PlayersPage";
import Protected from "../../components/Protected";
import InlineToggleCell from "../../components/InlineToggleCell";
import TransactionsGrid from "../../components/TransactionsGrid";
import {useAuthDisabled} from "../../context/AuthDisabledContext";

// Level derived from cumulative total XP against xp_level_config. Mirrors
// TransactionsGrid.levelForXp so the level shown here matches the level
// transitions on the Transactions grid below. Expects xpLevels sorted
// ascending by threshold; returns 1 when no config is available (every
// player is at least level 1, matching PlayerXpDto's default).
const levelForXp = (xpLevels, xp) => {
    if (xp == null) return null;
    let level = 1;
    for (const cfg of xpLevels) {
        if (cfg.xpThreshold <= xp) level = cfg.levelNumber;
    }
    return level;
};

// --- Add Asset Modal Component ---
const AddAssetModal = ({open, onClose, onAdd, assetList, title, assetLabel, isSubmitting}) => {
    const [selectedAssetId, setSelectedAssetId] = useState('');
    const [amount, setAmount] = useState(1);
    const [errors, setErrors] = useState({});

    const validate = () => {
        const newErrors = {};
        if (!selectedAssetId) newErrors.asset = `A ${assetLabel.toLowerCase()} must be selected.`;
        if (!amount || amount <= 0) newErrors.amount = 'Amount must be a positive number.';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (validate()) {
            onAdd(selectedAssetId, amount);
        }
    };

    const handleClose = () => {
        setSelectedAssetId('');
        setAmount(1);
        setErrors({});
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{mt: 1}}>
                    <FormControl fullWidth error={!!errors.asset}>
                        <InputLabel>{assetLabel}</InputLabel>
                        <Select
                            value={selectedAssetId}
                            label={assetLabel}
                            onChange={(e) => {
                                setSelectedAssetId(e.target.value);
                                if (errors.asset) setErrors(prev => ({...prev, asset: null}));
                            }}
                        >
                            {assetList.map((asset) => (
                                <MenuItem key={asset.id} value={asset.id}>
                                    <Stack direction="row" alignItems="center" spacing={1.5}>
                                        <Box component="img" src={asset.iconUrl || asset.imageUrl} alt={asset.name}
                                             sx={{width: 24, height: 24, borderRadius: '50%'}}/>
                                        <Typography variant="body1">{asset.name}</Typography>
                                    </Stack>
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.asset && <FormHelperText>{errors.asset}</FormHelperText>}
                    </FormControl>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Amount"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={amount}
                        onChange={(e) => {
                            setAmount(parseInt(e.target.value, 10) || 1);
                            if (errors.amount) setErrors(prev => ({...prev, amount: null}));
                        }}
                        error={!!errors.amount}
                        helperText={errors.amount || ''}
                        slotProps={{htmlInput: {min: 1}}}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={isSubmitting}>
                    {isSubmitting ? <CircularProgress size={24}/> : 'Add'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// XP has no entity to pick — just an amount — so it can't reuse AddAssetModal.
// Backend: POST /api/players/{uid}/xp/add/{amount} (@IsAdmin).
const AddXpModal = ({open, onClose, onAdd, isSubmitting}) => {
    const [amount, setAmount] = useState(100);
    const [error, setError] = useState(null);

    const handleSubmit = () => {
        if (!amount || amount <= 0) {
            setError('Amount must be a positive number.');
            return;
        }
        onAdd(amount);
    };

    const handleClose = () => {
        setAmount(100);
        setError(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
            <DialogTitle>Add XP to Player</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="XP Amount"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={amount}
                    onChange={(e) => {
                        setAmount(parseInt(e.target.value, 10) || 0);
                        if (error) setError(null);
                    }}
                    error={!!error}
                    helperText={error || 'XP is cumulative; the level is derived from the XP level config.'}
                    slotProps={{htmlInput: {min: 1}}}
                    sx={{mt: 1}}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={isSubmitting}>
                    {isSubmitting ? <CircularProgress size={24}/> : 'Add'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// --- Reusable Sub-Components ---

const Row = ({label, value}) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" sx={{fontWeight: 'bold'}}>{label}:</Typography>
        {typeof value === 'string' || typeof value === 'number'
            ? <Typography variant="body2">{value}</Typography>
            : value}
    </Stack>
);

const ProfileCard = ({player, level, totalXp, onUpdated}) => (
    <Paper elevation={2} sx={{p: 2, height: '100%'}}>
        <Typography variant="h6" gutterBottom>Profile Information</Typography>
        <Divider sx={{mb: 2}}/>
        <Stack spacing={1.5}>
            <Row label="ID" value={player.id || '—'}/>
            <Row label="Name" value={player.name || '—'}/>
            <Row label="Nickname" value={player.nickName || '—'}/>
            <Row label="Email" value={player.email || '—'}/>
            <Row label="Status" value={
                <InlineToggleCell
                    protectedBy={PlayersPage}
                    protectAction="update"
                    value={player.active}
                    save={(next) => apiService.put(
                        `/api/players/${player.id}/active`,
                        {value: next},
                    )}
                    onSaved={onUpdated}
                    tooltip="Click to activate / deactivate"
                />
            }/>
            <Divider sx={{my: 1}}/>
            <Row label="Level" value={
                level == null
                    ? '—'
                    : <Chip label={`Level ${level}`} color="info" size="small"/>
            }/>
            <Row label="Total XP" value={totalXp == null ? '—' : totalXp.toLocaleString()}/>
            <Divider sx={{my: 1}}/>
            <Row label="Online" value={
                <Chip
                    label={player.online ? 'Online' : 'Offline'}
                    color={player.online ? 'success' : 'default'}
                    variant={player.online ? 'filled' : 'outlined'}
                    size="small"
                />
            }/>
            <Row label="Joined"
                 value={player.createdAt ? new Date(player.createdAt).toLocaleString() : '—'}/>
            <Row label="Last Seen"
                 value={player.lastSeenAt ? new Date(player.lastSeenAt).toLocaleString() : '—'}/>
            <Row label="Last Updated"
                 value={player.updatedAt ? new Date(player.updatedAt).toLocaleString() : '—'}/>
            <Row label="Last Logout"
                 value={player.logoutAt ? new Date(player.logoutAt).toLocaleString() : '—'}/>
        </Stack>
    </Paper>
);

// Activate / Deactivate moved into ProfileCard as an InlineToggleCell on
// the Status row — the dead button + handler-less click here was confusing.
// ActionsCard now only carries actions that DO open a workflow (modals).
const ActionsCard = ({onAddCurrencyClick, onAddRewardClick, onAddGeltClick, onAddXpClick}) => {
    const isDisabled = useAuthDisabled();

    return (
        <Paper elevation={2} sx={{p: 2}}>
            <Typography variant="h6" gutterBottom>Admin Actions</Typography>
            <Divider sx={{mb: 2}}/>
            <Grid container spacing={1}>
                <Grid size={{xs: 12, sm: "auto"}}>
                    <Button
                        fullWidth
                        variant="outlined"
                        disabled={isDisabled}
                        startIcon={<AddCircleOutlineIcon/>}
                        onClick={onAddCurrencyClick}>
                        Add Currency
                    </Button>
                </Grid>
                <Grid size={{xs: 12, sm: "auto"}}>
                    <Button
                        fullWidth
                        variant="outlined"
                        disabled={isDisabled}
                        startIcon={<AddCircleOutlineIcon/>}
                        onClick={onAddRewardClick}>
                        Add Reward
                    </Button>
                </Grid>
                <Grid size={{xs: 12, sm: "auto"}}>
                    <Button
                        fullWidth
                        variant="outlined"
                        disabled={isDisabled}
                        startIcon={<AddCircleOutlineIcon/>}
                        onClick={onAddGeltClick}>
                        Add Gelt
                    </Button>
                </Grid>
                <Grid size={{xs: 12, sm: "auto"}}>
                    <Button
                        fullWidth
                        variant="outlined"
                        disabled={isDisabled}
                        startIcon={<AddCircleOutlineIcon/>}
                        onClick={onAddXpClick}>
                        Add XP
                    </Button>
                </Grid>
            </Grid>
        </Paper>
    );
}

// One consolidated card listing all three balance entity types (currencies,
// rewards, gelts) — easier to scan than three stacked cards and lines up
// better with the new Transactions panel below.
const BalancesSection = ({title, items, hrefPrefix}) => (
    <Box>
        <Typography variant="overline" sx={{color: 'text.secondary', letterSpacing: 1}}>{title}</Typography>
        <List dense disablePadding>
            {items?.length ? items.map(it => (
                <ListItem key={it.id} disableGutters sx={{py: 0.5}}>
                    <Typography>
                        <Link component={RouterLink} to={`${hrefPrefix}/${it.id}`}>{it.name}</Link>: {it.balance}
                    </Typography>
                </ListItem>
            )) : <ListItem disableGutters sx={{py: 0.5}}><Typography color="text.secondary">None</Typography></ListItem>}
        </List>
    </Box>
);

const BalancesCard = ({currencies, rewards, gelts}) => (
    <Paper elevation={2} sx={{p: 2, height: '100%'}}>
        <Typography variant="h6" gutterBottom>Balances</Typography>
        <Divider sx={{mb: 2}}/>
        <Stack spacing={2}>
            <BalancesSection title="Currencies" items={currencies} hrefPrefix="/currencies"/>
            <BalancesSection title="Rewards"    items={rewards}    hrefPrefix="/rewards"/>
            <BalancesSection title="Gelts"      items={gelts}      hrefPrefix="/gelts"/>
        </Stack>
    </Paper>
);

const GamesTable = ({playerId}) => {
    // This component's internal logic is correct and remains unchanged.
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [rowCount, setRowCount] = useState(0);
    const [pagination, setPagination] = useState({pageIndex: 0, pageSize: 5});
    const [sorting, setSorting] = useState([{id: 'startedAt', desc: true}]);
    const fetchData = useCallback(() => {
        setIsLoading(true);
        const params = new URLSearchParams({page: pagination.pageIndex, size: pagination.pageSize});
        if (sorting.length > 0) params.append('sort', `${sorting[0].id},${sorting[0].desc ? 'desc' : 'asc'}`);
        apiService.get(`/api/players/${playerId}/games?${params.toString()}`)
            .then(page => {
                setData(page.content);
                setRowCount(page.totalElements);
                setIsError(false);
            })
            .catch(() => setIsError(true))
            .finally(() => setIsLoading(false));
    }, [playerId, pagination, sorting]);
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    const columns = useMemo(() => [
        {
            accessorKey: 'id',
            header: 'Game ID',
            size: 300,
            Cell: ({cell}) => <Link component={RouterLink} to={`/games/${cell.getValue()}`}>{cell.getValue()}</Link>
        },
        {
            accessorKey: 'machineId',
            header: 'Machine',
            Cell: ({cell}) => <Link component={RouterLink} to={`/machines/${cell.getValue()}`}>{cell.getValue()}</Link>
        },
        {
            accessorKey: 'status',
            header: 'Status',
            Cell: ({cell}) => <Chip label={cell.getValue()} color={getStatusColor(cell.getValue())} size="small"/>
        },
        {
            accessorKey: 'winning',
            header: 'Result',
            Cell: ({cell}) => <Chip label={cell.getValue() || 'N/A'} color={getWinningColor(cell.getValue())}
                                    size="small"/>
        },
        {accessorKey: 'startedAt', header: 'Started', Cell: ({cell}) => new Date(cell.getValue()).toLocaleString()},
        {
            accessorKey: 'finishedAt',
            header: 'Finished',
            Cell: ({cell}) => cell.getValue() ? new Date(cell.getValue()).toLocaleString() : '—'
        },
    ], []);
    const table = useMaterialReactTable({
        columns, data, manualPagination: true, manualSorting: true, rowCount,
        state: {pagination, sorting, isLoading, showAlertBanner: isError},
        onPaginationChange: setPagination, onSortingChange: setSorting,
        muiToolbarAlertBannerProps: isError ? {color: 'error', children: 'Error loading games'} : undefined,
    });
    return <MaterialReactTable table={table}/>;
};


// --- Main Page Component ---

const PlayerDetailPage = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const [player, setPlayer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [masterCurrencies, setMasterCurrencies] = useState([]);
    const [masterRewards, setMasterRewards] = useState([]);
    const [masterGelts, setMasterGelts] = useState([]);
    // Sorted ascending by threshold — levelForXp() relies on that ordering.
    const [xpLevels, setXpLevels] = useState([]);

    // Modal state
    const [isCurrencyModalOpen, setCurrencyModalOpen] = useState(false);
    const [isRewardModalOpen, setRewardModalOpen] = useState(false);
    const [isGeltModalOpen, setGeltModalOpen] = useState(false);
    const [isXpModalOpen, setXpModalOpen] = useState(false);

    // Action state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({open: false, message: '', severity: 'success'});

    const currencyButtonRef = useRef(null);
    const rewardButtonRef = useRef(null);

    // Function to re-fetch player data
    const fetchPlayerData = useCallback(() => {
        apiService.get(`/api/players/${id}`)
            .then(setPlayer)
            .catch(() => setSnackbar({open: true, message: 'Failed to refresh player data.', severity: 'error'}));
    }, [id]);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            apiService.get(`/api/players/${id}`),
            apiService.get('/api/currencies'),
            apiService.get('/api/rewards'),
            apiService.get('/api/gelts'),
        ])
            .then(([playerData, currenciesData, rewardsData, geltsData]) => {
                setPlayer(playerData);
                setMasterCurrencies(currenciesData);
                setMasterRewards(rewardsData);
                setMasterGelts(geltsData);
            })
            .catch(err => setError(err.message || 'Failed to load page data.'))
            .finally(() => setLoading(false));
    }, [id]);

    // Deliberately NOT part of the Promise.all above: the level config is secondary
    // data. If it fails we still render the player (level falls back to 1) rather
    // than blanking the whole page with an error.
    useEffect(() => {
        apiService.get('/api/xp-level-configs')
            .then(levels => setXpLevels([...levels].sort((a, b) => a.xpThreshold - b.xpThreshold)))
            .catch(() => setXpLevels([]));
    }, []);

// --- Action Handlers with API Calls ---
    // All three share the same shape: POST /api/players/{uid}/{kind}/{entityId}/add/{amount}.
    // Backend wraps balance update + transaction-log write in one @Transactional,
    // so re-fetching the player after success shows a consistent snapshot.
    const postBalanceChange = async (kind, entityId, amount, label, closeModal) => {
        setIsSubmitting(true);
        try {
            await apiService.post(`/api/players/${id}/${kind}/${entityId}/add/${amount}`);
            setSnackbar({open: true, message: `${label} added successfully!`, severity: 'success'});
            fetchPlayerData();
            closeModal();
        } catch (err) {
            setSnackbar({open: true, message: err.message || `Failed to add ${label.toLowerCase()}.`, severity: 'error'});
        } finally {
            setIsSubmitting(false);
        }
    };
    const handleAddCurrency = (currencyId, amount) =>
        postBalanceChange('currencies', currencyId, amount, 'Currency', () => setCurrencyModalOpen(false));
    const handleAddReward = (rewardId, amount) =>
        postBalanceChange('rewards', rewardId, amount, 'Reward', () => setRewardModalOpen(false));
    const handleAddGelt = (geltId, amount) =>
        postBalanceChange('gelts', geltId, amount, 'Gelt', () => setGeltModalOpen(false));

    // XP endpoint has no entity segment (/xp/add/{amount}), so it can't reuse postBalanceChange.
    const handleAddXp = async (amount) => {
        setIsSubmitting(true);
        try {
            await apiService.post(`/api/players/${id}/xp/add/${amount}`);
            setSnackbar({open: true, message: 'XP added successfully!', severity: 'success'});
            fetchPlayerData();
            setXpModalOpen(false);
        } catch (err) {
            setSnackbar({open: true, message: err.message || 'Failed to add XP.', severity: 'error'});
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) return <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}><CircularProgress/></Box>;
    if (error) return <Alert severity="error" sx={{m: 2}}>{error}</Alert>;
    if (!player) return <Alert severity="info" sx={{m: 2}}>No such player.</Alert>;

    const totalXp = player.totalXp ?? 0;
    const level = levelForXp(xpLevels, totalXp);

    return (
        <Box sx={{p: 2}}>
            <Typography variant="h4" gutterBottom>Player #{id}</Typography>

            <Grid container spacing={3}>
                <Grid size={{xs: 12, md: 6}}>
                    <Stack spacing={3}>
                        <ProfileCard player={player} level={level} totalXp={totalXp} onUpdated={setPlayer}/>
                        <Protected component={PlayersPage} action="update" mode="disable">
                            <ActionsCard
                                onAddCurrencyClick={() => setCurrencyModalOpen(true)}
                                onAddRewardClick={() => setRewardModalOpen(true)}
                                onAddGeltClick={() => setGeltModalOpen(true)}
                                onAddXpClick={() => setXpModalOpen(true)}
                            />
                        </Protected>
                    </Stack>
                </Grid>
                <Grid size={{xs: 12, md: 6}}>
                    <BalancesCard
                        currencies={player.currencies}
                        rewards={player.rewards}
                        gelts={player.gelts}
                    />
                </Grid>
            </Grid>

            <Box sx={{mt: 4}}>
                <Typography variant="h5" gutterBottom>Games by this Player</Typography>
                <GamesTable playerId={id}/>
            </Box>

            <Box sx={{mt: 4}}>
                <Typography variant="h5" gutterBottom>Transactions</Typography>
                <Typography variant="body2" sx={{mb: 2, color: 'text.secondary'}}>
                    Balance changes + activity for this player. Today (UTC) by default — use the Type filter to narrow.
                </Typography>
                <TransactionsGrid playerUid={id}/>
            </Box>

            <AddAssetModal
                open={isCurrencyModalOpen}
                onClose={() => setCurrencyModalOpen(false)}
                onAdd={handleAddCurrency}
                assetList={masterCurrencies}
                title="Add Currency to Player"
                assetLabel="Currency"
                isSubmitting={isSubmitting}
            />
            <AddAssetModal
                open={isGeltModalOpen}
                onClose={() => setGeltModalOpen(false)}
                onAdd={handleAddGelt}
                assetList={masterGelts}
                title="Add Gelt to Player"
                assetLabel="Gelt"
                isSubmitting={isSubmitting}
            />
            <AddAssetModal
                open={isRewardModalOpen}
                onClose={() => setRewardModalOpen(false)}
                onAdd={handleAddReward}
                assetList={masterRewards}
                title="Add Reward to Player"
                assetLabel="Reward"
                isSubmitting={isSubmitting}
            />
            <AddXpModal
                open={isXpModalOpen}
                onClose={() => setXpModalOpen(false)}
                onAdd={handleAddXp}
                isSubmitting={isSubmitting}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({...snackbar, open: false})}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            >
                <Alert
                    onClose={() => setSnackbar({...snackbar, open: false})}
                    severity={snackbar.severity}
                    sx={{width: '100%'}}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <Box sx={{mt: 3}}>
                <Button onClick={() => navigate(-1)} variant="outlined" startIcon={<ArrowBackIcon/>}>
                    Back to Players
                </Button>
            </Box>
        </Box>
    );
};

PlayerDetailPage.abilities = {
    read: [ROLES.SUPPORT, ROLES.ADMINS, ROLES.VIEWERS],
    create: [ROLES.ADMINS],
    delete: [ROLES.ADMINS],
    update: [ROLES.ADMINS],
};

export default PlayerDetailPage;